/**
 * Пастельный сородич цвета — тот же оттенок, разбавленный белым.
 *
 * На уроке яркий цвет и его пастель нужны парой: ярким пишут и исправляют,
 * пастельным подчёркивают и закрашивают, не перебивая написанное. Держать
 * ради этого второй список цветов незачем — пастель считается из самого
 * цвета, и строка в окошке остаётся той же.
 */

/** Насколько разбавляем белым. Подобрано на глаз: цвет ещё узнаётся. */
export const PASTEL_MIX = 0.62;

type Rgb = [number, number, number];

function parseHex(color: string): Rgb | null {
  const s = color.trim().toLowerCase();
  if (s[0] !== "#") return null;
  const body = s.slice(1);
  if (body.length === 3) {
    const r = body[0]!;
    const g = body[1]!;
    const b = body[2]!;
    return parseHex("#" + r + r + g + g + b + b);
  }
  if (body.length !== 6) return null;
  if (!/^[0-9a-f]{6}$/.test(body)) return null;
  return [
    parseInt(body.slice(0, 2), 16),
    parseInt(body.slice(2, 4), 16),
    parseInt(body.slice(4, 6), 16),
  ];
}

function toHex(rgb: Rgb): string {
  return "#" + rgb.map((c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, "0")).join("");
}

/** Светлота по тому же правилу, что и везде на доске. */
function lightness(rgb: Rgb): number {
  return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
}

/**
 * Пастель для цвета. `null` — если разбавлять нечего (белый и почти белый)
 * или цвет задан не шестнадцатеричной записью: в таком случае пары просто
 * не будет, а строка не поедет.
 */
export function pastelOf(color: string, mix: number = PASTEL_MIX): string | null {
  const rgb = parseHex(color);
  if (!rgb) return null;
  if (lightness(rgb) > 0.85) return null;
  const soft = rgb.map((c) => Math.round(c + (255 - c) * mix)) as Rgb;
  return toHex(soft);
}
